// src/admin/platform.client.ts — consola de administrare (#/administrare).
// Adaptivă: MASTER ('super') vede tenant-uri + useri + membri pe orice tenant;
// owner/admin-ul unui org își gestionează membrii propriilor org-uri.

import "../styles.css"
import van from "vanjs-core"
import { session, load_session } from "../lib/session.client"
import { api_get, api_post, api_patch, api_delete } from "../lib/api.client"
import { copy_to_clipboard, mcp_url, mcp_url_org } from "../lib/workspace.client"
import { password_field } from "../lib/password.client"
import { get_hash_query_params } from "../lib/router.client"
import {
	SUPPORT_KINDS,
	SUPPORT_STATUS_LABEL_RO,
	SUPPORT_KIND_LABEL_RO,
	SUPPORT_TRANSITIONS,
	MAX_SUPPORT_BODY,
	type SupportStatus,
	type SupportContext,
} from "../lib/support"
import type { SupportThread, SupportThreadDetail, SupportPage, SupportPosted } from "../support"
import {
	TIERS,
	TIER_LABELS,
	TIER_DEFAULTS,
	FEATURE_GROUPS,
	FEATURE_LABELS,
	MODULE_ROLES,
	MODULE_ROLE_LABELS,
	ACTION_RIGHTS,
	ACTION_LABELS,
	ACTION_ROLES,
	effective_features,
	department_key,
	type FeatureFlag,
	type ActionRight,
	type RoleModules,
	type Tier,
} from "../lib/entitlements"
import {
	build_org_chart,
	org_chart_html,
	member_label,
	ROLE_BADGE,
	type OrgChartColumn,
} from "../lib/org_chart"

const t = van.tags

interface OrgAdminRow {
	id: string
	slug: string
	name: string
	status: string
	is_public: boolean
	balance: number
	member_count: number
	map_count: number
	// Config-ul BRUT de funcționalități (contract): null = fără config = totul activ.
	features?: { tier: Tier; flags?: Partial<Record<FeatureFlag, boolean>> } | null
}
interface UserAdminRow {
	id: string
	email: string
	display_name: string
	platform_role: string | null
	org_count: number
}
interface MemberRow {
	user_id: string
	email: string
	display_name: string
	role: string
	department?: string | null // departamentul în org (4.4, text liber, max 80)
}
/** Setările primăriei (self-service owner/admin sau super) — plan/contracts/departments.md + notify_switch.md. */
interface OrgSettings {
	role_modules: RoleModules | null
	notify_email: boolean
	departments: string[] // valorile distincte deja folosite pe membri (sugestii)
}

const card =
	"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-6"
const h2 = "text-lg font-bold text-slate-900 dark:text-slate-100 mb-3"
const input =
	"rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
// max-md:py-2 — țintă touch mai înaltă doar pe telefon (desktopul rămâne identic)
const btn =
	"px-3 py-1.5 max-md:py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 cursor-pointer disabled:opacity-60"
const btn_ghost =
	"px-2 py-1 max-md:py-2 rounded-md text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
const th = "text-left text-xs font-semibold text-slate-500 dark:text-slate-400 px-2 py-1.5"
const td =
	"px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200 border-t border-slate-100 dark:border-slate-800"

const is_super = () => session.val.user?.platform_role === "super"
const is_support_role = () => session.val.user?.platform_role === "support"

function msg_line() {
	const m = van.state("")
	const el = t.span(
		{ class: () => (m.val ? "ml-2 text-xs text-slate-500 dark:text-slate-400" : "hidden") },
		() => m.val,
	)
	const set = (s: string) => {
		m.val = s
		if (s) {
			setTimeout(() => (m.val = ""), 2500)
		}
	}
	return { el, set }
}

/**
 * Sursa UNICĂ de adevăr pentru lista de primării (org-uri), partajată de `TenantsSection`
 * (le afișează/creează) și `MembersSection` (le folosește în selector). Fără ea, cele două
 * secțiuni țineau liste separate, iar o primărie nou-adăugată nu apărea în „Membri pe
 * primărie" până la un reload de pagină.
 */
const orgs_state = van.state<OrgAdminRow[]>([])
async function reload_orgs(): Promise<void> {
	const r = await api_get<OrgAdminRow[]>("/api/admin/orgs")
	if (r.ok) {
		orgs_state.val = r.data
	}
}

/**
 * Org-urile pe care le poate administra userul curent, ca stare DERIVATĂ (deci reactivă):
 * super → lista partajată `orgs_state` (o primărie nou-adăugată apare imediat, fără reload);
 * altfel → org-urile în care e owner/admin (reactiv pe sesiune).
 */
function admin_org_options() {
	return van.derive(() =>
		is_super()
			? orgs_state.val.map((o) => ({ id: o.id, name: o.name }))
			: session.val.memberships
					.filter((m) => m.role === "owner" || m.role === "admin")
					.map((m) => ({ id: m.org_id, name: m.org_name })),
	)
}

// ── Useri (super) ────────────────────────────────────────────
function UsersSection() {
	const users = van.state<UserAdminRow[]>([])
	const email = van.state("")
	const password = van.state("")
	const display_name = van.state("")
	const { el: msg, set: say } = msg_line()

	async function load() {
		const r = await api_get<UserAdminRow[]>("/api/admin/users")
		if (r.ok) {
			users.val = r.data
		}
	}
	async function add() {
		// Numele e obligatoriu: apare în header și în răspunsurile trimise cetățenilor
		// (`staff_display`); fără el, operatorii apar generic ca „Operator primărie".
		if (!email.val.trim() || password.val.length < 8 || !display_name.val.trim()) {
			say("Nume + email + parolă ≥ 8 caractere")
			return
		}
		const r = await api_post("/api/admin/users", {
			email: email.val.trim(),
			password: password.val,
			display_name: display_name.val.trim(),
		})
		if (r.ok) {
			email.val = ""
			password.val = ""
			display_name.val = ""
			say("User creat ✓")
			await load()
		} else {
			say(r.err?.message ?? "Eroare")
		}
	}
	async function set_role(id: string, role: string) {
		const r = await api_patch(`/api/admin/users/${id}`, { platform_role: role || null })
		say(r.ok ? "Rol salvat ✓" : (r.err?.message ?? "Eroare"))
		await load()
	}

	const tbody = t.tbody()
	van.derive(() => {
		tbody.replaceChildren(
			...users.val.map((u) => {
				const sel = t.select(
					{
						class: input,
						onchange: (e: Event) => set_role(u.id, (e.target as HTMLSelectElement).value),
					},
					t.option({ value: "" }, "— niciunul —"),
					t.option({ value: "support" }, "support"),
					t.option({ value: "super" }, "super (MASTER)"),
				)
				sel.value = u.platform_role ?? ""
				return t.tr(
					t.td({ class: td }, u.email),
					t.td({ class: td }, String(u.org_count)),
					t.td({ class: td }, sel),
				)
			}),
		)
	})
	load()

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Utilizatori", msg),
		// wrapper overflow-x-auto: pe telefon tabelul derulează în card, nu lățește pagina
		t.div(
			{ class: "overflow-x-auto mb-3" },
			t.table(
				{ class: "w-full" },
				t.thead(
					t.tr(
						t.th({ class: th }, "Email"),
						t.th({ class: th }, "Primării"),
						t.th({ class: th }, "Rol platformă"),
					),
				),
				tbody,
			),
		),
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			t.input({
				class: input,
				placeholder: "Nume complet",
				maxlength: "120",
				value: display_name,
				oninput: (e: Event) => (display_name.val = (e.target as HTMLInputElement).value),
			}),
			t.input({
				class: input,
				type: "email",
				placeholder: "email user nou",
				value: email,
				oninput: (e: Event) => (email.val = (e.target as HTMLInputElement).value),
			}),
			// „arată parola" (lib partajat) — superul dictează parola inițială la telefon,
			// deci chiar vrea s-o vadă; `value: password` = golire reactivă după Adaugă.
			password_field({
				placeholder: "parolă inițială (≥8)",
				value: password,
				oninput: (v) => (password.val = v),
				compact: true,
			}).el,
			t.button({ class: btn, onclick: add }, "Adaugă user"),
		),
	)
}

// ── Membri pe tenant (super sau owner/admin) ─────────────────
// Lotul 3 — sincronizarea „Membri pe primărie" ↔ „Organigramă" (două secțiuni, aceeași listă): fiecare
// anunță schimbările ei printr-un contor, cealaltă se reîncarcă (fără buclă: reîncărcarea nu anunță nimic).
const members_tick = van.state(0) // membrii s-au schimbat din TABEL (rol, departament, adăugare, eliminare)
const chart_tick = van.state(0) // membrii s-au schimbat din ORGANIGRAMĂ (card tras într-o altă coloană)

function MembersSection(fixed_org?: () => string) {
	// org-urile pe care le poate administra: super → toate; altfel cele cu rol owner/admin.
	const selected = van.state("")
	const members = van.state<MemberRow[]>([])
	const email = van.state("")
	const role = van.state("editor")
	const new_department = van.state("")
	// Departamentele deja folosite în org (sugestii pentru câmpul liber) — din GET settings (4.4).
	const departments = van.state<string[]>([])
	const dl_dep_id = `dep-sugestii-${Math.random().toString(36).slice(2, 8)}`
	const dl_dep = t.datalist({ id: dl_dep_id })
	van.derive(() => {
		dl_dep.replaceChildren(...departments.val.map((d) => t.option({ value: d })))
	})
	const { el: msg, set: say } = msg_line()

	const opts = admin_org_options()
	async function load_members() {
		if (!selected.val) {
			members.val = []
			departments.val = []
			return
		}
		const org = selected.val
		const [r, s] = await Promise.all([
			api_get<MemberRow[]>(`/api/orgs/${org}/members`),
			api_get<OrgSettings>(`/api/orgs/${org}/settings`),
		])
		if (org !== selected.val) {
			return // răspuns întârziat pentru alt org
		}
		members.val = r.ok ? r.data : []
		departments.val = s.ok && Array.isArray(s.data.departments) ? s.data.departments : []
	}
	async function add() {
		if (!email.val.trim()) {
			return
		}
		const dep = new_department.val.trim()
		const r = await api_post(`/api/orgs/${selected.val}/members`, {
			email: email.val.trim(),
			role: role.val,
			...(dep ? { department: dep } : {}),
		})
		if (r.ok) {
			email.val = ""
			new_department.val = ""
			say("Adăugat ✓")
			await load_members()
			members_tick.val++
		} else {
			say(r.err?.message ?? "Eroare")
		}
	}
	async function change_role(uid: string, new_role: string) {
		const r = await api_patch(`/api/orgs/${selected.val}/members/${uid}`, { role: new_role })
		say(r.ok ? "Rol salvat ✓" : (r.err?.message ?? "Eroare"))
		await load_members()
		if (r.ok) {
			members_tick.val++
		}
	}
	/**
	 * Departamentul membrului (4.4): text liber; gol = fără departament (serverul îl face null).
	 * Rândul se actualizează PE LOC (fără re-fetch pe membri — tbody-ul s-ar reconstrui și tab-ul
	 * de la un câmp la următorul pierdea focusul); se reîmprospătează DOAR sugestiile datalist-ului.
	 * „Curentul" se citește din obiectul rândului, nu dintr-un snapshot — a doua editare a aceluiași
	 * câmp trebuie comparată cu valoarea deja salvată.
	 */
	async function change_department(m: MemberRow, inp: HTMLInputElement) {
		const org = selected.val
		const current = m.department ?? ""
		const next = inp.value.trim()
		if (next === current) {
			inp.value = current
			return
		}
		const r = await api_patch(`/api/orgs/${org}/members/${m.user_id}`, {
			department: next || null,
		})
		if (!r.ok) {
			say(r.err?.message ?? "Eroare")
			inp.value = current
			return
		}
		say("Departament salvat ✓")
		m.department = next || null
		inp.value = next
		members_tick.val++
		const s = await api_get<OrgSettings>(`/api/orgs/${org}/settings`)
		if (s.ok && org === selected.val && Array.isArray(s.data.departments)) {
			departments.val = s.data.departments
		}
	}
	async function remove(uid: string) {
		const r = await api_delete(`/api/orgs/${selected.val}/members/${uid}`)
		say(r.ok ? "Eliminat ✓" : (r.err?.message ?? "Eroare"))
		await load_members()
		if (r.ok) {
			members_tick.val++
		}
	}
	// Un card tras în organigramă schimbă departamentul → rândul de aici se reîncarcă (nu e focalizat
	// în timpul unei trageri, deci reconstruirea tbody-ului nu pierde nimic). Fără citiri de state în
	// corpul derive-ului în afara contorului: `load_members` citește `selected` abia în microtask.
	van.derive(() => {
		if (chart_tick.val > 0) {
			queueMicrotask(() => void load_members())
		}
	})

	// Modul FIȘĂ: org-ul vine de sus (fisa_org) — selectorul propriu e ascuns.
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load_members()
			}
		})
	}

	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			load_members()
		},
	})
	van.derive(() => {
		const list = opts.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		// Ține selecția VALIDĂ: la prima populare (sau dacă primăria aleasă dispare din listă)
		// sare pe prima și reîncarcă membrii. A doua trecere e stabilă (condiția devine falsă).
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load_members()
		}
		org_select.value = selected.val
	})

	const tbody = t.tbody()
	van.derive(() => {
		tbody.replaceChildren(
			...members.val.map((m) => {
				const sel = t.select(
					{
						class: input,
						onchange: (e: Event) => change_role(m.user_id, (e.target as HTMLSelectElement).value),
					},
					...["viewer", "editor", "admin", "owner"].map((r) => t.option({ value: r }, r)),
				)
				sel.value = m.role
				// Câmp liber cu sugestii (departamentele deja folosite); se salvează la ieșirea din câmp
				// sau la Enter — nu la fiecare tastă (PATCH per literă ar fi zgomot în istoric).
				const dep = t.input({
					class: input + " w-40",
					placeholder: "—",
					title: "Departamentul membrului (text liber, max 80 de caractere)",
					list: dl_dep_id,
					maxlength: "80",
					value: m.department ?? "",
					onchange: (e: Event) => void change_department(m, e.target as HTMLInputElement),
					onkeydown: (e: KeyboardEvent) => {
						if (e.key === "Enter") {
							e.preventDefault()
							;(e.target as HTMLInputElement).blur()
						}
					},
				})
				return t.tr(
					// id-ul rândului = ținta „focalizează membrul" din organigramă (Lotul 3)
					{ id: `member-row-${m.user_id}` },
					t.td({ class: td }, m.email),
					t.td({ class: td }, sel),
					t.td({ class: td }, dep),
					t.td(
						{ class: td },
						t.button(
							{
								class: btn_ghost + " text-red-600 dark:text-red-400",
								// Confirmare ca la orice acțiune distructivă (documente, șabloane, iconițe):
								// eliminarea e un DELETE pe membership, fără istoric — un clic greșit lăsa
								// colegul fără acces pe loc (constatare din auditul manualelor, 15.09).
								onclick: () => {
									if (
										!confirm(
											`Elimini ${m.email} din primărie?\nContul rămâne, dar pierde imediat accesul la hărțile și documentele primăriei. Îl poți adăuga din nou oricând.`,
										)
									) {
										return
									}
									void remove(m.user_id)
								},
							},
							"Elimină",
						),
					),
				)
			}),
		)
	})

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Membri pe primărie", msg),
		dl_dep,
		t.div(
			{ class: "mb-3 flex items-center gap-2" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
		),
		t.div(
			{ class: "overflow-x-auto mb-3" },
			t.table(
				{ class: "w-full" },
				t.thead(
					t.tr(
						t.th({ class: th }, "Email"),
						t.th({ class: th }, "Rol"),
						t.th({ class: th }, "Departament"),
						t.th({ class: th }, ""),
					),
				),
				tbody,
			),
		),
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			t.input({
				class: input,
				type: "email",
				placeholder: "email membru (cont existent)",
				value: email,
				oninput: (e: Event) => (email.val = (e.target as HTMLInputElement).value),
			}),
			t.input({
				class: input + " w-40",
				type: "text",
				placeholder: "departament (opțional)",
				list: dl_dep_id,
				maxlength: "80",
				value: new_department,
				oninput: (e: Event) => (new_department.val = (e.target as HTMLInputElement).value),
			}),
			(() => {
				const sel = t.select(
					{
						class: input,
						onchange: (e: Event) => (role.val = (e.target as HTMLSelectElement).value),
					},
					...["viewer", "editor", "admin", "owner"].map((r) => t.option({ value: r }, r)),
				)
				// van.derive => citirea lui role.val stă în scope-ul ei, nu în funcția reactivă
				// din AdminConsole; altfel schimbarea rolului ar re-monta toată secțiunea.
				van.derive(() => (sel.value = role.val))
				return sel
			})(),
			t.button({ class: btn, onclick: add }, "Adaugă membru"),
		),
	)
}

// ── Organigrama (Lotul 3, brief 12.10) ──
// Caietele: „sistemul va permite definirea organigramei într-o manieră vizuală; fiecare departament va
// avea asociați utilizatori, alocați pe roluri". Varianta minimă: primăria sus, o coloană per departament
// (nomenclatorul din setări ∪ departamentele de pe membri, potrivite prin `department_key` — lib/org_chart,
// PUR), cardurile membrilor cu insigna rolului, „Fără departament" ultima, numărul de membri per coloană,
// tipărire (HTML self-contained într-o fereastră nouă, deschisă SINCRON — anti pop-up blocker, tiparul din
// lib/print) și TRAGEREA unui card dintr-o coloană în alta = PATCH-ul de membru existent (atomic, fără cod
// nou pe server), cu actualizarea pe loc a cardului și a rândului din „Membri pe primărie" (`chart_tick`).
// Click pe card → focalizează rândul membrului în tabel (editarea rămâne acolo). Fără ierarhie între
// departamente. Tragerea e cu mouse-ul (HTML5 drag & drop) — pe telefon rămâne câmpul din tabel.
// ⚠️ Van: secțiunea e construită în legătura reactivă a paginii (AdminConsole) — nu citește state în corp,
// doar în `van.derive`-uri proprii și în handler-e (regula din CLAUDE.md, F14 T2).
function OrgChartSection(fixed_org?: () => string) {
	const selected = van.state("")
	const members = van.state<MemberRow[]>([])
	const departments = van.state<string[]>([])
	const { el: msg, set: say } = msg_line()
	const opts = admin_org_options()
	let columns: OrgChartColumn[] = []

	async function load() {
		const org = selected.val
		if (!org) {
			members.val = []
			departments.val = []
			return
		}
		const [r, s] = await Promise.all([
			api_get<MemberRow[]>(`/api/orgs/${org}/members`),
			api_get<OrgSettings>(`/api/orgs/${org}/settings`),
		])
		if (org !== selected.val) {
			return
		}
		members.val = r.ok ? r.data : []
		departments.val = s.ok && Array.isArray(s.data.departments) ? s.data.departments : []
	}
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load()
			}
		})
	}
	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			void load()
		},
	})
	van.derive(() => {
		const list = opts.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load()
		}
		org_select.value = selected.val
	})
	// Schimbările din tabelul de membri (rol, departament, adăugare, eliminare) se reflectă aici.
	van.derive(() => {
		if (members_tick.val > 0) {
			queueMicrotask(() => void load())
		}
	})

	const org_name = () => opts.val.find((o) => o.id === selected.val)?.name ?? "Primăria"

	/** Rândul membrului din „Membri pe primărie" — derulat la vedere și evidențiat două secunde. */
	function focus_member_row(uid: string): void {
		const row = document.getElementById(`member-row-${uid}`)
		if (!row) {
			return
		}
		row.scrollIntoView({ behavior: "smooth", block: "center" })
		row.classList.add("bg-cyan-50", "dark:bg-cyan-900/30")
		setTimeout(() => row.classList.remove("bg-cyan-50", "dark:bg-cyan-900/30"), 2000)
	}
	/** Cardul tras într-o coloană: departamentul membrului = titlul coloanei (null pe „Fără departament"). */
	async function move(uid: string, col: OrgChartColumn): Promise<void> {
		const m = members.val.find((x) => x.user_id === uid)
		if (!m) {
			return
		}
		const current_key = m.department ? department_key(m.department) : ""
		if (current_key === col.key) {
			return
		}
		const next = col.key === "" ? null : col.title
		const r = await api_patch(`/api/orgs/${selected.val}/members/${uid}`, { department: next })
		if (!r.ok) {
			say(r.err?.message ?? "Eroare")
			return
		}
		m.department = next
		members.val = [...members.val] // re-randare pe loc
		say("Departament salvat ✓")
		chart_tick.val++
	}

	const DROP_RING = ["ring-2", "ring-cyan-500"]
	function card_el(m: MemberRow): HTMLElement {
		return t.div(
			{
				class:
					"rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 mb-1.5 cursor-grab active:cursor-grabbing hover:border-cyan-400",
				draggable: "true",
				title: "Trage în altă coloană ca să schimbi departamentul; click = rândul din tabel",
				ondragstart: (e: DragEvent) => {
					e.dataTransfer?.setData("text/plain", m.user_id)
					if (e.dataTransfer) {
						e.dataTransfer.effectAllowed = "move"
					}
				},
				onclick: () => focus_member_row(m.user_id),
			},
			t.div(
				{ class: "text-sm font-semibold text-slate-800 dark:text-slate-100 truncate" },
				member_label({ display_name: m.display_name, email: m.email }),
			),
			t.div(
				{ class: "flex items-center gap-2" },
				t.span(
					{
						class:
							"text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-cyan-50 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200",
					},
					ROLE_BADGE[m.role] ?? m.role,
				),
				t.span({ class: "text-[11px] text-slate-400 dark:text-slate-500 truncate" }, m.email),
			),
		)
	}
	function column_el(col: OrgChartColumn): HTMLElement {
		const el: HTMLElement = t.div(
			{
				class:
					"min-w-[215px] w-56 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-2",
				ondragover: (e: DragEvent) => {
					e.preventDefault()
					el.classList.add(...DROP_RING)
				},
				ondragleave: () => el.classList.remove(...DROP_RING),
				ondrop: (e: DragEvent) => {
					e.preventDefault()
					el.classList.remove(...DROP_RING)
					const uid = e.dataTransfer?.getData("text/plain") ?? ""
					if (uid) {
						void move(uid, col)
					}
				},
			},
			t.div(
				{
					class:
						"flex items-center justify-between gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 pb-1.5 mb-2",
				},
				t.span({ class: "truncate" }, col.title),
				t.span(
					{ class: "font-normal text-slate-400 dark:text-slate-500" },
					String(col.members.length),
				),
			),
			...(col.members.length === 0
				? [
						t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500 italic px-1 py-2" },
							"— niciun membru —",
						),
					]
				: col.members.map((m) => card_el(m as MemberRow))),
		)
		return el
	}

	const org_box = t.span({
		class:
			"inline-block rounded-lg border-2 border-cyan-700 px-4 py-1.5 font-bold text-slate-900 dark:text-slate-100",
	})
	const board = t.div({ class: "flex gap-3 overflow-x-auto pb-2 items-start" })
	van.derive(() => {
		columns = build_org_chart(
			members.val.map((m) => ({
				user_id: m.user_id,
				email: m.email,
				display_name: m.display_name,
				role: m.role,
				department: m.department ?? null,
			})),
			departments.val,
		)
		org_box.textContent = org_name()
		board.replaceChildren(...columns.map(column_el))
	})
	function print_chart(): void {
		// Fereastra se deschide SINCRON, în clic (altfel browserul o blochează ca pop-up), apoi primește
		// documentul self-contained din lib/org_chart.
		const w = window.open("", "_blank")
		if (!w) {
			say("Browserul a blocat fereastra de tipărire.")
			return
		}
		w.document.write(org_chart_html(org_name(), columns, new Date().toLocaleDateString("ro-RO")))
		w.document.close()
		w.focus()
		setTimeout(() => w.print(), 300)
	}

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Organigramă", msg),
		t.div(
			{ class: "mb-3 flex items-center gap-2 flex-wrap" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
			t.button({ class: btn_ghost, onclick: print_chart }, "Tipărește"),
		),
		t.div({ class: "text-center mb-3" }, org_box),
		board,
		t.p(
			{ class: "text-xs text-slate-400 dark:text-slate-500 mt-2" },
			"O coloană per departament (cele din setări și cele de pe membri), plus „Fără departament”. Trage un card " +
				"în altă coloană ca să muți membrul (se salvează imediat); click pe card deschide rândul lui în tabelul de " +
				"membri, unde se schimbă rolul.",
		),
	)
}

// ── Setările primăriei (self-service owner/admin, sau super pe fișa org-ului) ──
// 4.4(b): matricea rol × modul (`role_modules` — modulele OPRITE per rol, aplicate PESTE contract;
// owner-ul nu e restricționabil); 4.10(a): comutatorul notificărilor pe email la solicitări.
// Lotul 2 (12.10): aceeași matrice are și rândurile de ACȚIUNI (export/import/partajare/ștergere —
// lib/entitlements ACTION_RIGHTS), comutabile doar pe rolurile din ACTION_ROLES; aceeași politică.
// Contracte: plan/contracts/departments.md, plan/contracts/notify_switch.md, drepturi_rol_strat_atribut.md.
function OrgSettingsSection(fixed_org?: () => string) {
	const selected = van.state("")
	const settings = van.state<OrgSettings | null>(null)
	// Starea încărcării e SEPARATĂ de date: `null` nu mai înseamnă „se încarcă" — un GET eșuat
	// arăta „Se încarcă…" la nesfârșit, iar bifa de notificări apărea bifată (și activă) deși
	// valoarea era necunoscută; un „Salvează" pe matricea goală ar fi trimis `role_modules: null`.
	const load_state = van.state<"loading" | "ready" | "error">("loading")
	const busy = van.state(false)
	const { el: msg, set: say } = msg_line()
	const opts = admin_org_options()

	async function load() {
		const org = selected.val
		if (!org) {
			settings.val = null
			load_state.val = "loading"
			return
		}
		if (settings.val === null) {
			load_state.val = "loading"
		}
		const r = await api_get<OrgSettings>(`/api/orgs/${org}/settings`)
		if (org !== selected.val) {
			return
		}
		if (!r.ok) {
			settings.val = null
			load_state.val = "error"
			say(r.err?.message ?? "Eroare")
			return
		}
		settings.val = r.data
		load_state.val = "ready"
	}
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load()
			}
		})
	}
	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			void load()
		},
	})
	van.derive(() => {
		const list = opts.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load()
		}
		org_select.value = selected.val
	})

	async function patch(body: Record<string, unknown>, ok_msg: string) {
		if (!selected.val || busy.val) {
			return
		}
		busy.val = true
		const r = await api_patch(`/api/orgs/${selected.val}/settings`, body)
		say(r.ok ? ok_msg : (r.err?.message ?? "Eroare"))
		await load()
		busy.val = false
	}

	// Comutatorul notificărilor (4.10) — se salvează la bifare; DEZACTIVAT până sosesc setările.
	const notify_chk = t.input({
		type: "checkbox",
		class: "accent-cyan-700",
		disabled: () => busy.val || load_state.val !== "ready",
		onchange: (e: Event) => {
			const checked = (e.target as HTMLInputElement).checked
			void patch(
				{ notify_email: checked },
				checked ? "Notificări pornite ✓" : "Notificări oprite ✓",
			)
		},
	}) as HTMLInputElement
	van.derive(() => {
		const s = settings.val
		notify_chk.checked = s !== null && s.notify_email !== false
	})

	// Matricea rol × modul: bifă = modulul e PERMIS rolului (implicit); debifat = oprit (`false`).
	// Corpul se re-randează la schimbarea setărilor (replaceChildren pe nod stabil, ca FeaturesSection).
	const matrix = t.div()
	const checks = new Map<string, HTMLInputElement>() // `${rol}:${flag}` → bifă
	van.derive(() => {
		const s = settings.val
		const state = load_state.val
		checks.clear()
		if (!s) {
			matrix.replaceChildren(
				t.p(
					{ class: "text-sm text-slate-400 dark:text-slate-500" },
					state === "error" ? "Setările nu s-au putut încărca." : "Se încarcă…",
				),
			)
			return
		}
		const rm = s.role_modules ?? {}
		const head = t.tr(
			t.th({ class: th }, "Modul"),
			...MODULE_ROLES.map((r) => t.th({ class: th + " text-center" }, MODULE_ROLE_LABELS[r])),
		)
		const rows_el = FEATURE_GROUPS.flatMap((g) => [
			t.tr(
				t.td(
					{
						class:
							td +
							" text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500",
						colspan: String(MODULE_ROLES.length + 1),
					},
					g.label,
				),
			),
			...g.flags.map((f) =>
				t.tr(
					t.td({ class: td }, FEATURE_LABELS[f]),
					...MODULE_ROLES.map((r) => {
						const chk = t.input({
							type: "checkbox",
							class: "accent-cyan-700",
							checked: rm[r]?.[f] !== false,
						}) as HTMLInputElement
						checks.set(`${r}:${f}`, chk)
						return t.td({ class: td + " text-center" }, chk)
					}),
				),
			),
		])
		// Lotul 2: rândurile de ACȚIUNI — bifă doar pe rolurile pe care acțiunea e comutabilă (editor; viewer
		// doar la export); pe celelalte „—" (administratorul nu e restricționabil, ca proprietarul).
		const action_rows = [
			t.tr(
				t.td(
					{
						class:
							td +
							" text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500",
						colspan: String(MODULE_ROLES.length + 1),
					},
					"Acțiuni",
				),
			),
			...ACTION_RIGHTS.map((a) =>
				t.tr(
					t.td({ class: td }, ACTION_LABELS[a]),
					...MODULE_ROLES.map((r) => {
						if (!ACTION_ROLES[a].includes(r)) {
							return t.td({ class: td + " text-center text-slate-300 dark:text-slate-600" }, "—")
						}
						const chk = t.input({
							type: "checkbox",
							class: "accent-cyan-700",
							checked: rm[r]?.[a] !== false,
						}) as HTMLInputElement
						checks.set(`${r}:${a}`, chk)
						return t.td({ class: td + " text-center" }, chk)
					}),
				),
			),
		]
		matrix.replaceChildren(
			t.div(
				{ class: "overflow-x-auto" },
				t.table({ class: "w-full" }, t.thead(head), t.tbody(...rows_el, ...action_rows)),
			),
		)
	})
	function save_matrix() {
		// Doar debifările se trimit (`false`); un rol fără nicio debifare nu apare; nimic → null.
		const out: RoleModules = {}
		for (const [key, chk] of checks) {
			if (!chk.checked) {
				const [role, flag] = key.split(":") as [keyof RoleModules, FeatureFlag | ActionRight]
				out[role] = { ...out[role], [flag]: false }
			}
		}
		void patch(
			{ role_modules: Object.keys(out).length > 0 ? out : null },
			"Drepturi pe module salvate ✓",
		)
	}

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Setările primăriei", msg),
		t.div(
			{ class: "mb-3 flex items-center gap-2" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
		),
		t.label(
			{
				class:
					"flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none mb-4",
			},
			t.span({ class: "mt-0.5" }, notify_chk),
			t.span(
				"Notificări pe email la solicitări",
				t.span(
					{ class: "block text-xs text-slate-400 dark:text-slate-500" },
					"Când e oprită, primăria și cetățenii nu mai primesc emailuri pentru solicitări; totul rămâne vizibil în aplicație. " +
						"Nu afectează resetarea parolei sau trimiterea hărții pe email.",
				),
			),
		),
		t.div(
			{ class: "text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1" },
			"Drepturi pe module, pe rol",
		),
		t.p(
			{ class: "text-xs text-slate-400 dark:text-slate-500 mb-2" },
			"Bifă = modulul e disponibil rolului (în limita contractului primăriei — un rol nu poate primi mai mult decât " +
				"contractul). Debifează ca să oprești un modul pentru un rol; proprietarul (owner) vede întotdeauna tot. " +
				"Fără portal extern, sesizările, consultarea și documentele publice se opresc și ele pentru acel rol. " +
				"Rândurile „Acțiuni” opresc exportul, importul, partajarea (publicare, embed-uri, trimitere pe email) sau " +
				"ștergerea obiectelor pentru editori (exportul și pentru vizualizatori); drepturile pe strat și pe atribut " +
				"se dau din Admin → Straturi → Editează.",
		),
		matrix,
		t.div(
			{ class: "mt-3 flex items-center gap-2" },
			t.button(
				{
					class: btn,
					disabled: () => busy.val || load_state.val !== "ready",
					onclick: save_matrix,
				},
				"Salvează drepturile",
			),
			t.button(
				{
					class: btn_ghost,
					disabled: () => busy.val || load_state.val !== "ready",
					onclick: () => void patch({ role_modules: null }, "Restricții șterse ✓"),
					title: "Șterge toate restricțiile pe rol (toate modulele din contract, pentru toți)",
				},
				"Permite tot",
			),
		),
	)
}

// ── Funcționalități per contract (super) — tier + override-uri per primărie ──
// Decizie COMERCIALĂ, doar la nivel de platformă: primăria nu vede și nu atinge ecranul.
// Bifele arată valoarea EFECTIVĂ; proveniența e marcată („din tier" vs „override").
function FeaturesSection(fixed_org?: () => string) {
	const selected = van.state("")
	const busy = van.state(false)
	const { el: msg, set: say } = msg_line()

	const current = () => orgs_state.val.find((o) => o.id === selected.val)

	async function save(
		features: { tier: Tier; flags?: Partial<Record<FeatureFlag, boolean>> } | null,
	) {
		if (!selected.val || busy.val) {
			return
		}
		// `busy` acoperă TOATĂ tranzacția (PATCH + reload) — eliberat mai devreme, bifele s-ar
		// reactiva pe starea VECHE din orgs_state și o a doua bifă ar putea ANULA override-ul
		// tocmai salvat (lost-update, prins la review-ul adversarial).
		busy.val = true
		const r = await api_patch(`/api/admin/orgs/${selected.val}`, { features })
		say(r.ok ? "Salvat ✓" : (r.err?.message ?? "Eroare"))
		await reload_orgs() // bifele se re-randează din lista partajată (sursa unică de adevăr)
		// Sesiunea poartă feature-urile per org (gating-ul de UI: butonul 3D, nav-ul de
		// solicitări) — fără re-hidratare, harta ar judeca pe snapshot-ul de la load.
		if (r.ok) {
			await load_session()
		}
		busy.val = false
	}

	// Modul FIȘĂ: org-ul vine de sus (fisa_org) — selectorul propriu e ascuns.
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
			}
		})
	}

	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
		},
	})
	van.derive(() => {
		const list = orgs_state.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		// Ține selecția VALIDĂ (vezi MembersSection): prima populare / primărie dispărută.
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
		}
		org_select.value = selected.val
	})

	// Corpul se re-randează integral la schimbarea org-ului/config-ului (replaceChildren pe nod
	// stabil — pattern-ul tbody-urilor de mai sus; nu întoarcem null din funcții reactive).
	const body = t.div()
	van.derive(() => {
		const o = current()
		if (!o) {
			body.replaceChildren()
			return
		}
		const cfg = o.features ?? null
		const tier: Tier | "" = cfg?.tier ?? ""
		const flags = cfg?.flags ?? {}
		const eff = effective_features(cfg)

		const tier_select = t.select(
			{
				class: input,
				disabled: () => busy.val,
				onchange: (e: Event) => {
					const v = (e.target as HTMLSelectElement).value
					// Schimbarea tier-ului PĂSTREAZĂ override-urile (sunt abateri contractuale explicite);
					// „fără configurație" le șterge cu tot cu tier (features = null → totul activ).
					void save(v === "" ? null : { tier: v as Tier, flags })
				},
			},
			t.option({ value: "" }, "— fără configurație (totul activ) —"),
			...TIERS.map((x) => t.option({ value: x }, TIER_LABELS[x])),
		)
		tier_select.value = tier

		const flag_row = (f: FeatureFlag) => {
			const overridden = flags[f] !== undefined
			// Sub-flag-urile pex_* sunt subordonate: fără portal_extern efectiv, rămân forțate pe
			// oprit (serverul le calculează la fel) — bifa se dezactivează ca UI-ul să nu mintă.
			const forced_off = f !== "portal_extern" && f.startsWith("pex_") && !eff.portal_extern
			const chk = t.input({
				type: "checkbox",
				class: "accent-cyan-700",
				// `busy` DOAR prin binding pe funcție: citit static, derive-ul corpului ar depinde de
				// el și fiecare salvare ar re-randa integral secțiunea de 2 ori în plus (flicker +
				// checkbox-ul „sare înapoi" pe starea veche cât ține request-ul).
				disabled: () => tier === "" || forced_off || busy.val,
				onchange: () => {
					if (tier === "") {
						return
					}
					const next = !eff[f]
					const new_flags: Partial<Record<FeatureFlag, boolean>> = { ...flags }
					if (next === TIER_DEFAULTS[tier][f]) {
						delete new_flags[f] // înapoi pe valoarea tier-ului → override-ul dispare
					} else {
						new_flags[f] = next
					}
					void save({ tier, flags: new_flags })
				},
			}) as HTMLInputElement
			chk.checked = eff[f]
			return t.label(
				{
					class:
						"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none",
				},
				chk,
				FEATURE_LABELS[f],
				overridden
					? t.span(
							{ class: "text-[10px] font-semibold text-amber-600 dark:text-amber-400" },
							"override ✎",
						)
					: t.span(
							{ class: "text-[10px] text-slate-400 dark:text-slate-500" },
							tier === "" ? "" : "din tier",
						),
				forced_off
					? t.span(
							{ class: "text-[10px] text-slate-400 dark:text-slate-500" },
							"(cere Hartă publică + embed)",
						)
					: t.span({ style: "display:none" }),
			)
		}

		body.replaceChildren(
			t.div(
				{ class: "mb-3 flex items-center gap-2" },
				t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Tier:"),
				tier_select,
			),
			tier === ""
				? t.p(
						{ class: "text-xs text-slate-500 dark:text-slate-400 mb-2" },
						"Fără configurație, organizația are TOT activ (compat retro). Alege un tier ca să poți opri funcționalități.",
					)
				: t.span({ style: "display:none" }),
			t.div(
				{ class: "grid gap-4 sm:grid-cols-2" },
				...FEATURE_GROUPS.map((g) =>
					t.div(
						t.div(
							{
								class:
									"text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5",
							},
							g.label,
						),
						t.div({ class: "flex flex-col gap-1.5" }, ...g.flags.map(flag_row)),
					),
				),
			),
		)
	})

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Funcționalități per contract", msg),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-3" },
			"Ce e oprit aici e refuzat de server (nu doar ascuns în UI). Sursa: matricea contractuală ",
			"(MASTER xlsx, foaia „1. Proiecte platforma”); seed: scripts/seed_org_features.ts.",
		),
		t.div(
			{ class: "mb-3 flex items-center gap-2" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
		),
		body,
	)
}

// ── Billing per tenant (super) — sold, grant manual, alocații lunare, jurnal ──
function BillingSection(fixed_org?: () => string) {
	const selected = van.state("")
	const busy = van.state(false)
	const { el: msg, set: say } = msg_line()

	interface Account {
		balance: number
		plan_allowance: Record<string, number>
		cycle_used: Record<string, number>
		free_until: string | null
		enforced: boolean
		costs: Record<string, number>
	}
	interface Ledger {
		id: string
		delta: number
		balance_after: number
		reason: string
		source: string
		author: string | null
		created_at: string
	}
	const account = van.state<Account | null>(null)
	const ledger = van.state<Ledger[]>([])
	const grant_amount = van.state("")
	const grant_note = van.state("")

	async function load() {
		account.val = null
		ledger.val = []
		if (!selected.val) {
			return
		}
		const a = await api_get<Account>(`/api/orgs/${selected.val}/billing`)
		account.val = a.ok ? a.data : null
		const l = await api_get<Ledger[]>(`/api/orgs/${selected.val}/billing/ledger`)
		ledger.val = l.ok ? l.data : []
	}
	async function grant() {
		const delta = Number(grant_amount.val)
		if (!selected.val || busy.val || !Number.isInteger(delta) || delta === 0) {
			say("Introdu un număr întreg nenul (negativ = corecție).")
			return
		}
		busy.val = true
		const r = await api_post(`/api/admin/orgs/${selected.val}/credits`, {
			delta,
			note: grant_note.val.trim() || undefined,
		})
		say(r.ok ? "Creditat ✓" : (r.err?.message ?? "Eroare"))
		grant_amount.val = ""
		grant_note.val = ""
		await load()
		busy.val = false
	}
	async function save_allowance(raw: string) {
		if (!selected.val || busy.val) {
			return
		}
		let parsed: unknown
		try {
			parsed = raw.trim() === "" ? null : JSON.parse(raw)
		} catch {
			say("JSON invalid.")
			return
		}
		busy.val = true
		const r = await api_patch(`/api/admin/orgs/${selected.val}`, { plan_allowance: parsed })
		say(r.ok ? "Alocații salvate ✓" : (r.err?.message ?? "Eroare"))
		await load()
		busy.val = false
	}

	// Modul FIȘĂ: org-ul vine de sus (fisa_org) — selectorul propriu e ascuns.
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load()
			}
		})
	}

	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			void load()
		},
	})
	van.derive(() => {
		const list = orgs_state.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load()
		}
		org_select.value = selected.val
	})

	const allowance_input = t.input({
		class: input + " flex-1 font-mono text-[11px]",
		placeholder: '{"generate_document": 50}',
	}) as HTMLInputElement

	const body = t.div()
	van.derive(() => {
		const a = account.val
		if (!a) {
			body.replaceChildren(
				t.p({ class: "text-sm text-slate-400 dark:text-slate-500" }, "Se încarcă / fără date."),
			)
			return
		}
		allowance_input.value = Object.keys(a.plan_allowance).length
			? JSON.stringify(a.plan_allowance)
			: ""
		const allowance_rows = Object.entries(a.plan_allowance).map(([action, cap]) =>
			t.div(
				{ class: "text-xs text-slate-600 dark:text-slate-300" },
				`${action}: ${a.cycle_used[action] ?? 0}/${cap} folosite luna asta`,
			),
		)
		body.replaceChildren(
			t.div(
				{ class: "flex flex-wrap items-center gap-4 mb-3" },
				t.div(
					t.div(
						{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500" },
						"Sold credite",
					),
					t.div(
						{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100" },
						String(a.balance),
					),
				),
				t.div(
					t.div(
						{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500" },
						"Taxare",
					),
					t.div(
						{
							class: a.enforced
								? "text-sm font-semibold text-red-600 dark:text-red-400"
								: "text-sm font-semibold text-emerald-600 dark:text-emerald-400",
						},
						a.enforced
							? "ACTIVĂ"
							: a.free_until && Date.parse(a.free_until) > Date.now()
								? `evaluare până la ${a.free_until.slice(0, 10)}`
								: "oprită (BILLING_ENFORCE)",
					),
				),
				t.div(
					t.div(
						{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500" },
						"Tarife",
					),
					t.div(
						{ class: "text-xs text-slate-600 dark:text-slate-300" },
						Object.entries(a.costs)
							.map(([k, v]) => `${k}: ${v}`)
							.join(" · ") || "—",
					),
				),
			),
			allowance_rows.length
				? t.div({ class: "mb-3 flex flex-col gap-0.5" }, ...allowance_rows)
				: t.span({ style: "display:none" }),
			// Grant manual
			t.div(
				{ class: "flex items-center gap-2 flex-wrap mb-3" },
				t.input({
					class: input + " w-28",
					placeholder: "+100 / -10",
					value: grant_amount,
					oninput: (e: Event) => (grant_amount.val = (e.target as HTMLInputElement).value),
				}),
				t.input({
					class: input + " flex-1",
					placeholder: "notă (ex. contract nr. …)",
					value: grant_note,
					oninput: (e: Event) => (grant_note.val = (e.target as HTMLInputElement).value),
				}),
				t.button({ class: btn, disabled: () => busy.val, onclick: grant }, "Creditează"),
			),
			// Alocații lunare (JSON mic — {acțiune: nr/lună})
			t.div(
				{ class: "flex items-center gap-2 mb-3" },
				t.span({ class: "text-xs text-slate-500 dark:text-slate-400" }, "Alocații/lună:"),
				allowance_input,
				t.button(
					{
						class: btn_ghost,
						disabled: () => busy.val,
						onclick: () => save_allowance(allowance_input.value),
					},
					"Salvează",
				),
			),
			// Jurnal (ultimele mișcări)
			t.div(
				{ class: "max-h-48 overflow-auto" },
				t.table(
					{ class: "w-full" },
					t.thead(
						t.tr(
							t.th({ class: th }, "Data"),
							t.th({ class: th }, "Δ"),
							t.th({ class: th }, "Sold"),
							t.th({ class: th }, "Motiv"),
							t.th({ class: th }, "Autor"),
						),
					),
					t.tbody(
						...ledger.val.map((e) =>
							t.tr(
								t.td({ class: td + " whitespace-nowrap" }, e.created_at.slice(0, 16)),
								t.td(
									{
										class:
											td +
											(e.delta < 0
												? " text-red-600 dark:text-red-400"
												: " text-emerald-700 dark:text-emerald-300"),
									},
									(e.delta > 0 ? "+" : "") + String(e.delta),
								),
								t.td({ class: td }, String(e.balance_after)),
								t.td({ class: td }, `${e.reason} (${e.source})`),
								t.td({ class: td + " text-slate-400 dark:text-slate-500" }, e.author ?? "—"),
							),
						),
					),
				),
			),
		)
	})

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Credite & facturare", msg),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-3" },
			"Soldul + jurnalul org-ului. Taxarea efectivă e OPT-IN pe instanță (env BILLING_ENFORCE=1); ",
			"fereastra de evaluare (free_until) nu se taxează. Plata cu cardul (Polar/Stripe) vine separat.",
		),
		t.div(
			{ class: "mb-3 flex items-center gap-2" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
		),
		body,
	)
}

// ── Utilizare per tenant (super/support) — cifrele de produs/vânzare ──
function UsageSection(fixed_org?: () => string) {
	const selected = van.state("")
	const days = van.state(30)
	interface Usage {
		days: number
		by_kind: Record<string, number>
		by_day: Array<{ day: string; count: number }>
		top_meta: Array<{ kind: string; label: string; count: number }>
	}
	const usage = van.state<Usage | null>(null)

	const KIND_LABELS: Record<string, string> = {
		login: "Autentificări",
		map_view: "Hărți deschise (intern)",
		public_map_view: "Vizualizări portal public",
		regim: "Interogări de regim",
		document_generated: "Acte generate",
		request_created: "Solicitări depuse",
		mcp_tool: "Acțiuni AI (MCP)",
	}

	async function load() {
		usage.val = null
		if (!selected.val) {
			return
		}
		const r = await api_get<Usage>(`/api/admin/orgs/${selected.val}/usage?days=${days.val}`)
		usage.val = r.ok ? r.data : null
	}

	// Modul FIȘĂ: org-ul vine de sus (fisa_org) — selectorul propriu e ascuns.
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load()
			}
		})
	}

	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			void load()
		},
	})
	van.derive(() => {
		const list = orgs_state.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load()
		}
		org_select.value = selected.val
	})

	const body = t.div()
	van.derive(() => {
		const u = usage.val
		if (!u) {
			body.replaceChildren(
				t.p({ class: "text-sm text-slate-400 dark:text-slate-500" }, "Fără date încă."),
			)
			return
		}
		const total = Object.values(u.by_kind).reduce((a, b) => a + b, 0)
		const cards = Object.entries(u.by_kind).map(([kind, n]) =>
			t.div(
				{ class: "rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" },
				t.div({ class: "text-lg font-extrabold text-slate-900 dark:text-slate-100" }, String(n)),
				t.div(
					{ class: "text-[10px] text-slate-500 dark:text-slate-400" },
					KIND_LABELS[kind] ?? kind,
				),
			),
		)
		body.replaceChildren(
			t.div(
				{ class: "text-xs text-slate-500 dark:text-slate-400 mb-2" },
				`${total} evenimente în ultimele ${u.days} zile`,
			),
			t.div({ class: "flex flex-wrap gap-2 mb-3" }, ...cards),
			u.top_meta.length
				? t.div(
						t.div(
							{
								class:
									"text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1",
							},
							"Cele mai folosite hărți",
						),
						...u.top_meta.map((m) =>
							t.div(
								{ class: "text-xs text-slate-600 dark:text-slate-300" },
								`${m.label} — ${m.count}× (${KIND_LABELS[m.kind] ?? m.kind})`,
							),
						),
					)
				: t.span({ style: "display:none" }),
		)
	})

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Utilizare"),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-3" },
			"Cifre de utilizare per primărie (anonime — fără date personale): argumentul de vânzare ",
			"și semnalul de adopție. Sursa: usage_events (server-side).",
		),
		t.div(
			{ class: "mb-3 flex items-center gap-2 flex-wrap" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
			t.select(
				{
					class: input,
					onchange: (e: Event) => {
						days.val = Number((e.target as HTMLSelectElement).value)
						void load()
					},
				},
				t.option({ value: "7" }, "7 zile"),
				t.option({ value: "30", selected: true }, "30 zile"),
				t.option({ value: "90" }, "90 zile"),
			),
		),
		body,
	)
}

// ── Acces AI (MCP) per tenant — token + link-uri de conectare ─
function McpSection(fixed_org?: () => string) {
	const opts = admin_org_options()
	const selected = van.state("")
	const token = van.state("") // tokenul curent al org-ului ("" = negenerat)
	const map_slug = van.state("") // pentru link-ul per-hartă
	const busy = van.state(false)
	const { el: msg, set: say } = msg_line()
	async function load_token() {
		token.val = ""
		if (!selected.val) {
			return
		}
		const r = await api_get<{ token: string | null }>(`/api/orgs/${selected.val}/mcp-token`)
		token.val = r.ok ? (r.data.token ?? "") : ""
	}
	async function regen() {
		if (!selected.val || busy.val) {
			return
		}
		busy.val = true
		const r = await api_post<{ token: string }>(`/api/orgs/${selected.val}/mcp-token`, {})
		busy.val = false
		if (r.ok) {
			token.val = r.data.token
			say("Token (re)generat ✓")
		} else {
			say(r.err?.message ?? "Eroare")
		}
	}
	async function copy(text: string) {
		if (!text) {
			return
		}
		say((await copy_to_clipboard(text)) ? "Copiat ✓" : "Nu am putut copia")
	}

	function link_field(get_url: () => string) {
		return t.div(
			{ class: "flex items-center gap-2 mb-1 flex-wrap" },
			t.input({
				class: input + " flex-1 min-w-40 font-mono text-[11px]",
				readonly: true,
				value: get_url,
			}),
			t.button({ class: btn_ghost, onclick: () => copy(get_url()) }, "Copiază"),
		)
	}

	// Modul FIȘĂ: org-ul vine de sus (fisa_org) — selectorul propriu e ascuns.
	if (fixed_org) {
		van.derive(() => {
			const v = fixed_org()
			if (v && selected.val !== v) {
				selected.val = v
				void load_token()
			}
		})
	}

	const org_select = t.select({
		class: input,
		onchange: (e: Event) => {
			selected.val = (e.target as HTMLSelectElement).value
			load_token()
		},
	})
	van.derive(() => {
		const list = opts.val
		org_select.replaceChildren(...list.map((o) => t.option({ value: o.id }, o.name)))
		// Ține selecția VALIDĂ (vezi MembersSection): prima populare / primărie dispărută.
		if (!list.some((o) => o.id === selected.val)) {
			selected.val = list.length > 0 ? list[0].id : ""
			void load_token()
		}
		org_select.value = selected.val
	})

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Acces AI (MCP)", msg),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-3" },
			"Linkul de conectare pentru ChatGPT/Claude. În clientul AI alegi „No Auth” și lipești ",
			"linkul — tokenul (semnat) e inclus în el.",
		),
		t.div(
			{ class: "mb-3 flex items-center gap-2" },
			...(fixed_org
				? []
				: [
						t.span({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Primărie:"),
						org_select,
					]),
		),
		// Mesaj când nu există token
		t.p(
			{ class: () => (token.val ? "hidden" : "text-sm text-slate-500 dark:text-slate-400 mb-2") },
			"Niciun token generat pentru această primărie.",
		),
		// Link organizație
		t.div(
			{ class: () => (token.val ? "mb-3" : "hidden") },
			t.div(
				{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1" },
				"Link pentru toată organizația (AI-ul alege harta):",
			),
			link_field(() => (token.val ? mcp_url_org(token.val) : "")),
		),
		// Link per-hartă
		t.div(
			{ class: () => (token.val ? "mb-3" : "hidden") },
			t.div(
				{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1" },
				"Link pentru o hartă anume (prepopulată din URL):",
			),
			t.div(
				{ class: "flex items-center gap-2 mb-1" },
				t.span({ class: "text-xs text-slate-500 dark:text-slate-400" }, "slug hartă:"),
				t.input({
					class: input,
					placeholder: "ex. pug-voiteg",
					value: map_slug,
					oninput: (e: Event) => (map_slug.val = (e.target as HTMLInputElement).value),
				}),
			),
			link_field(() => (map_slug.val && token.val ? mcp_url(map_slug.val, token.val) : "")),
		),
		// Generează / regenerează
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			t.button({ class: btn, disabled: busy, onclick: regen }, () =>
				token.val ? "Regenerează token" : "Generează token",
			),
			t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, () =>
				token.val ? "Regenerarea invalidează linkurile vechi (rotire)." : "",
			),
		),
	)
}

// ─────────────────────────────────────────────────────────────
// FIȘA PRIMĂRIEI (super): listă → o pagină per primărie cu TOT
// ─────────────────────────────────────────────────────────────

// ── Suport: tichetele utilizatorilor (F14) — super ȘI support (D16: aici support scrie) ────────
const SUPPORT_STATUS_CLS: Record<SupportStatus, string> = {
	chat: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
	escalated: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
	answered: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300",
	closed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
}
function support_badge(status: SupportStatus) {
	return t.span(
		{
			class: `inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${SUPPORT_STATUS_CLS[status] ?? SUPPORT_STATUS_CLS.chat}`,
		},
		SUPPORT_STATUS_LABEL_RO[status] ?? status,
	)
}
function fmt_when(iso: string | null): string {
	if (!iso) {
		return "—"
	}
	const d = new Date(iso)
	return isNaN(d.getTime())
		? "—"
		: d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })
}
/** Contextul tehnic al tichetului, lizibil: scalarii ca „cheie: valoare”, listele ca rânduri. */
function ContextBlock(ctx: SupportContext | null) {
	if (!ctx) {
		return t.div(
			{ class: "text-xs text-slate-400 dark:text-slate-500" },
			"Fără context tehnic (tichet trimis fără pagină).",
		)
	}
	const kv: Array<[string, string]> = []
	const add = (k: string, v: unknown) => {
		if (v !== undefined && v !== null && v !== "") {
			kv.push([k, Array.isArray(v) ? v.join(", ") : String(v)])
		}
	}
	add("Pagina", ctx.route)
	add("Harta", ctx.map)
	add("Strat", ctx.layer)
	add("Obiect", ctx.feature)
	add("Rol", ctx.role)
	add("Primăria", ctx.org_slug)
	add("Module pornite", ctx.features_on)
	add("Module oprite", ctx.features_off)
	add("Versiune", ctx.app_version)
	add("Browser", ctx.ua)
	add("Ecran", ctx.viewport)
	add("Online", ctx.online === undefined ? undefined : ctx.online ? "da" : "nu")
	add("Instalată (PWA)", ctx.standalone === undefined ? undefined : ctx.standalone ? "da" : "nu")
	add("Capturat la", ctx.captured_at ? fmt_when(ctx.captured_at) : undefined)
	const mono = "font-mono text-[11px] break-all"
	return t.div(
		{ class: "space-y-2" },
		t.dl(
			{ class: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs" },
			...kv.flatMap(([k, v]) => [
				t.dt({ class: "text-slate-500 dark:text-slate-400" }, k),
				t.dd({ class: "text-slate-800 dark:text-slate-200 break-words" }, v),
			]),
		),
		...(ctx.failed_api?.length
			? [
					t.div(
						t.div(
							{ class: "text-xs font-semibold text-slate-600 dark:text-slate-300 mt-1" },
							"Cereri API eșuate",
						),
						...ctx.failed_api.map((f) =>
							t.div(
								{ class: mono + " text-slate-700 dark:text-slate-200" },
								`${f.status} ${f.method} ${f.path}${f.ts ? ` · ${fmt_when(f.ts)}` : ""}`,
							),
						),
					),
				]
			: []),
		...(ctx.errors?.length
			? [
					t.div(
						t.div(
							{ class: "text-xs font-semibold text-slate-600 dark:text-slate-300 mt-1" },
							"Erori de client",
						),
						...ctx.errors.map((e) =>
							t.div(
								{ class: mono + " text-red-700 dark:text-red-300" },
								`${e.message}${e.source ? ` @ ${e.source}` : ""}${e.ts ? ` · ${fmt_when(e.ts)}` : ""}`,
							),
						),
					),
				]
			: []),
	)
}

function SupportSection() {
	const rows = van.state<SupportThread[]>([])
	const total = van.state(0)
	// Vederea implicită = ce cere un om (escaladatele). Conversațiile cu asistentul și cele închise au cipul lor.
	const f_status = van.state<string>("escalated")
	const counts = van.state<Record<string, number>>({})
	const f_kind = van.state("")
	const f_mine = van.state(false)
	let f_q = "" // textul căutării — în variabilă, nu în state (focusul)
	const selected = van.state<SupportThreadDetail | null>(null)
	const busy = van.state(false)
	const { el: msg, set: say } = msg_line()
	let reply_text = ""

	async function load() {
		const qs = new URLSearchParams()
		if (f_status.val) {
			qs.set("status", f_status.val)
		}
		if (f_kind.val) {
			qs.set("kind", f_kind.val)
		}
		if (f_mine.val) {
			qs.set("mine", "1")
		}
		if (f_q.trim()) {
			qs.set("q", f_q.trim())
		}
		qs.set("limit", "50")
		const r = await api_get<SupportPage>(`/api/admin/support?${qs.toString()}`)
		if (r.ok) {
			rows.val = r.data.items
			total.val = r.data.total
			counts.val = r.data.counts
		}
	}
	async function open(id: string) {
		const r = await api_get<SupportThreadDetail>(`/api/support/threads/${encodeURIComponent(id)}`)
		if (r.ok) {
			selected.val = r.data
			reply_text = ""
		} else {
			say(r.err?.message ?? "Tichetul nu e disponibil.")
		}
	}
	/** Rândul din listă se actualizează PE LOC (fără re-fetch); detaliul primește firul proaspăt. */
	function apply_thread(fresh: SupportThread) {
		rows.val = rows.val.map((x) => (x.id === fresh.id ? fresh : x))
		const cur = selected.val
		if (cur && cur.thread.id === fresh.id) {
			selected.val = { thread: fresh, messages: cur.messages }
		}
	}
	async function reply() {
		const cur = selected.val
		const body = reply_text.trim()
		if (!cur || !body || busy.val) {
			return
		}
		busy.val = true
		const r = await api_post<SupportPosted>(`/api/admin/support/${cur.thread.id}/reply`, { body })
		busy.val = false
		if (!r.ok) {
			say(r.err?.message ?? "Eroare")
			return
		}
		reply_text = ""
		say("Răspuns trimis ✓")
		apply_thread(r.data.thread)
		await open(cur.thread.id) // mesajul + eventualele mesaje de sistem
	}
	async function patch(p: { status?: string; kind?: string; assigned_to?: string | null }) {
		const cur = selected.val
		if (!cur || busy.val) {
			return
		}
		busy.val = true
		const r = await api_patch<SupportThread>(`/api/admin/support/${cur.thread.id}`, p)
		busy.val = false
		if (!r.ok) {
			say(r.err?.message ?? "Eroare")
			return
		}
		say("Salvat ✓")
		apply_thread(r.data)
		if (p.status) {
			await open(cur.thread.id)
		}
	}

	const select_cls = input + " text-xs"
	const STATUS_CHIPS: Array<[string, string]> = [
		["escalated", "De rezolvat"],
		["answered", "Răspunse"],
		["chat", "Cu asistentul"],
		["closed", "Închise"],
		["", "Toate"],
	]
	const chip_count = (value: string): number => {
		const c = counts.val
		return value ? (c[value] ?? 0) : Object.values(c).reduce((a, b) => a + b, 0)
	}
	const f_status_el = t.div(
		{ class: "flex items-center gap-1 flex-wrap" },
		...STATUS_CHIPS.map(([value, label]) =>
			t.button(
				{
					class: () =>
						"px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer border transition " +
						(f_status.val === value
							? "bg-cyan-700 text-white border-cyan-700"
							: "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"),
					onclick: () => {
						f_status.val = value
						void load()
					},
				},
				() => `${label} (${chip_count(value)})`,
			),
		),
	)
	const f_kind_el = t.select(
		{
			class: select_cls,
			onchange: (e: Event) => {
				f_kind.val = (e.target as HTMLSelectElement).value
				void load()
			},
		},
		t.option({ value: "" }, "toate tipurile"),
		...SUPPORT_KINDS.map((k) => t.option({ value: k }, SUPPORT_KIND_LABEL_RO[k])),
	)
	let q_timer: ReturnType<typeof setTimeout> | null = null
	const f_q_el = t.input({
		type: "search",
		class: input + " text-xs min-w-[12rem]",
		placeholder: "caută: subiect, cont, primărie",
		oninput: (e: Event) => {
			f_q = (e.target as HTMLInputElement).value
			if (q_timer) {
				clearTimeout(q_timer)
			}
			q_timer = setTimeout(() => void load(), 300)
		},
	})
	const f_mine_el = t.label(
		{
			class: "flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer",
		},
		t.input({
			type: "checkbox",
			onchange: (e: Event) => {
				f_mine.val = (e.target as HTMLInputElement).checked
				void load()
			},
		}),
		"preluate de mine",
	)

	const tbody = t.tbody()
	van.derive(() => {
		const sel_id = selected.val?.thread.id
		tbody.replaceChildren(
			...rows.val.map((row) =>
				t.tr(
					{
						class: () =>
							"cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" +
							(row.id === sel_id ? " bg-cyan-50 dark:bg-cyan-950/40" : ""),
						onclick: () => void open(row.id),
					},
					t.td({ class: td }, support_badge(row.status)),
					t.td({ class: td }, SUPPORT_KIND_LABEL_RO[row.kind] ?? row.kind),
					t.td({ class: td + " max-w-[22rem] truncate" }, row.subject),
					t.td({ class: td }, row.user_name || row.user_email),
					t.td({ class: td }, row.org_name || "—"),
					t.td({ class: td + " whitespace-nowrap" }, fmt_when(row.updated_at)),
					t.td({ class: td }, row.assigned_label || "—"),
				),
			),
		)
	})

	function Detail(d: SupportThreadDetail) {
		const thread = d.thread
		const me = session.val.user?.id ?? ""
		const ta = t.textarea({
			class: input + " w-full min-h-[4.5rem]",
			placeholder:
				thread.status === "closed"
					? "Firul e închis — redeschide-l din stare ca să răspunzi"
					: "Răspuns către utilizator…",
			maxlength: String(MAX_SUPPORT_BODY),
			oninput: (e: Event) => {
				reply_text = (e.target as HTMLTextAreaElement).value
			},
		})
		const status_sel = t.select(
			{
				class: select_cls,
				onchange: (e: Event) => {
					const v = (e.target as HTMLSelectElement).value
					if (v && v !== thread.status) {
						void patch({ status: v })
					}
				},
			},
			t.option(
				{ value: thread.status, selected: true },
				`stare: ${SUPPORT_STATUS_LABEL_RO[thread.status]}`,
			),
			...SUPPORT_TRANSITIONS[thread.status].map((s) =>
				t.option({ value: s }, `→ ${SUPPORT_STATUS_LABEL_RO[s]}`),
			),
		)
		const kind_sel = t.select(
			{
				class: select_cls,
				onchange: (e: Event) => {
					const v = (e.target as HTMLSelectElement).value
					if (v !== thread.kind) {
						void patch({ kind: v })
					}
				},
			},
			...SUPPORT_KINDS.map((k) =>
				t.option({ value: k, selected: k === thread.kind }, `tip: ${SUPPORT_KIND_LABEL_RO[k]}`),
			),
		)
		return t.div(
			{
				class:
					"mt-5 border-t border-slate-200 dark:border-slate-700 pt-4 grid gap-5 lg:grid-cols-[1fr_minmax(16rem,22rem)]",
			},
			t.div(
				t.div(
					{ class: "flex items-center gap-2 flex-wrap mb-2" },
					support_badge(thread.status),
					t.span(
						{ class: "text-xs text-slate-500 dark:text-slate-400" },
						`${SUPPORT_KIND_LABEL_RO[thread.kind]}${thread.kind_corrected ? " (corectat)" : ""} · ${thread.source === "mcp" ? "asistent AI (MCP)" : "aplicație"} · motiv: ${thread.escalate_reason ?? "—"}`,
					),
				),
				t.h3(
					{ class: "text-base font-bold text-slate-900 dark:text-slate-100 mb-1" },
					thread.subject,
				),
				t.div(
					{ class: "text-xs text-slate-500 dark:text-slate-400 mb-3" },
					`${thread.user_name} · ${thread.user_email} · ${thread.org_name || "fără primărie"} · deschis ${fmt_when(thread.created_at)}`,
				),
				t.div(
					{ class: "space-y-2 rounded-xl bg-slate-50 dark:bg-slate-950 p-3" },
					...d.messages.map((m) =>
						m.author_role === "system"
							? t.div(
									{ class: "text-center text-[11px] text-slate-400 dark:text-slate-500 py-1" },
									`${m.body} · ${fmt_when(m.created_at)}`,
								)
							: t.div(
									{
										class: `flex ${m.author_role === "operator" ? "justify-end" : "justify-start"}`,
									},
									t.div(
										{
											class:
												"max-w-[85%] rounded-2xl px-3.5 py-2 " +
												(m.author_role === "operator"
													? "bg-cyan-700 text-white"
													: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200"),
										},
										t.div(
											{
												class: `text-[11px] font-semibold mb-0.5 ${m.author_role === "operator" ? "text-cyan-100" : "text-slate-500 dark:text-slate-400"}`,
											},
											m.author_label,
										),
										t.div({ class: "text-sm whitespace-pre-wrap break-words" }, m.body),
										...(m.author_role === "assistant" && m.meta
											? [
													t.div(
														{
															class:
																"text-[10px] mt-1 text-violet-700 dark:text-violet-300 break-words",
														},
														(() => {
															const dec = m.meta as {
																kind?: string
																confidence?: number
																mode?: string
																sections?: string[]
																usage?: { calls?: number; cached_tokens?: number }
																model?: string
															}
															return `decizie: ${dec.kind ?? "?"} · încredere ${typeof dec.confidence === "number" ? Math.round(dec.confidence * 100) + "%" : "?"} · mod ${dec.mode ?? "?"} · ${dec.usage?.calls ?? 1} apel(uri)${dec.model ? ` · ${dec.model}` : ""} · secțiuni: ${(dec.sections ?? []).join(", ") || "—"}`
														})(),
													),
												]
											: []),
										t.div(
											{
												class: `text-[10px] mt-1 ${m.author_role === "operator" ? "text-cyan-200" : "text-slate-400 dark:text-slate-500"}`,
											},
											fmt_when(m.created_at),
										),
									),
								),
					),
				),
				t.div(
					{ class: "mt-3 space-y-2" },
					ta,
					t.div(
						{ class: "flex items-center gap-2 flex-wrap" },
						t.button(
							{
								class: btn,
								disabled: () => busy.val || thread.status === "closed",
								onclick: () => void reply(),
							},
							"Răspunde",
						),
						status_sel,
						kind_sel,
						t.button(
							{
								class: btn_ghost,
								disabled: () => busy.val || thread.assigned_to === me,
								onclick: () => void patch({ assigned_to: me }),
							},
							thread.assigned_to === me ? "preluat de tine" : "Preia",
						),
					),
				),
			),
			t.div(
				t.div(
					{
						class:
							"text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2",
					},
					"Context tehnic",
				),
				ContextBlock(thread.context),
			),
		)
	}

	// deep-link din emailul către echipă: #/administrare?suport=<id>
	const want = get_hash_query_params().get("suport")
	// ⚠️ Încărcarea inițială pornește DUPĂ construcție (queueMicrotask), nu direct: `load()` citește
	// f_status/f_kind/f_mine SINCRON, înainte de primul await, iar secțiunea e construită ÎN funcția reactivă a
	// paginii (AdminConsole) — citirile înscriau filtrele ca dependențe ale PAGINII, care se reconstruia la
	// fiecare click pe un cip și remonta secțiunea pe „De rezolvat” (testare 07.10: cipurile, tipul și „preluate
	// de mine” nu mergeau; căutarea da — textul ei e variabilă simplă, nu state). Regula Van: nicio citire de
	// state în corpul unei componente construite într-o legătură a părintelui, doar în legături proprii.
	// soneria pe email către echipă (SUPPORT_MAIL_TEAM + adresă): când e oprită, inbox-ul o spune în antet
	const mail_team = van.state<boolean | null>(null)
	queueMicrotask(() => {
		if (want) {
			void open(want)
		}
		void load()
		void api_get<{ mail_team: boolean }>("/api/support/config").then((r) => {
			if (r.ok) {
				mail_team.val = r.data.mail_team
			}
		})
	})

	return t.div(
		{ class: card },
		t.div(
			{ class: "flex items-center justify-between flex-wrap gap-2 mb-3" },
			t.h2({ class: h2 + " mb-0" }, "Tichete de suport"),
			t.span(
				{ class: "text-xs text-slate-500 dark:text-slate-400" },
				() =>
					`${total.val} în total · escaladatele primele${mail_team.val === false ? " · emailul către echipă e oprit (doar inbox-ul)" : ""}`,
			),
		),
		t.div(
			{ class: "flex items-center gap-2 flex-wrap mb-3" },
			f_status_el,
			f_kind_el,
			f_q_el,
			f_mine_el,
			msg,
		),
		t.div(
			{ class: "overflow-x-auto" },
			t.table(
				{ class: "w-full" },
				t.thead(
					t.tr(
						t.th({ class: th }, "Stare"),
						t.th({ class: th }, "Tip"),
						t.th({ class: th }, "Subiect"),
						t.th({ class: th }, "Cont"),
						t.th({ class: th }, "Primăria"),
						t.th({ class: th }, "Actualizat"),
						t.th({ class: th }, "Preluat"),
					),
				),
				tbody,
			),
		),
		t.div(() =>
			rows.val.length === 0
				? t.div(
						{ class: "text-xs text-slate-400 dark:text-slate-500 py-3" },
						"Niciun tichet pe filtrele alese.",
					)
				: t.span(),
		),
		t.div(() => {
			const d = selected.val
			return d ? Detail(d) : t.span()
		}),
	)
}

const fisa_org = van.state("") // "" = vezi lista

function tier_of(o: OrgAdminRow): string {
	return o.features?.tier ? TIER_LABELS[o.features.tier] : "— (totul activ)"
}

function OrgListTable() {
	const slug = van.state("")
	const name = van.state("")
	const { el: msg, set: say } = msg_line()
	async function add() {
		if (!slug.val.trim() || !name.val.trim()) {
			return
		}
		const r = await api_post("/api/admin/orgs", { slug: slug.val.trim(), name: name.val.trim() })
		say(r.ok ? "Adăugat ✓" : (r.err?.message ?? "Eroare"))
		if (r.ok) {
			slug.val = ""
			name.val = ""
			await reload_orgs()
		}
	}
	const tbody = t.tbody()
	van.derive(() => {
		tbody.replaceChildren(
			...orgs_state.val.map((o) =>
				t.tr(
					{
						class: "hover:bg-cyan-50 dark:hover:bg-cyan-950 cursor-pointer",
						onclick: () => (fisa_org.val = o.id),
					},
					t.td({ class: td + " font-semibold text-cyan-800 dark:text-cyan-300" }, o.name),
					t.td({ class: td + " text-slate-400 dark:text-slate-500" }, tier_of(o)),
					t.td(
						{ class: td },
						t.span(
							{
								class:
									o.status === "active"
										? "text-emerald-600 dark:text-emerald-400 font-semibold"
										: "text-red-600 dark:text-red-400 font-semibold",
							},
							o.status === "active" ? "activă" : "suspendată",
						),
					),
					t.td({ class: td }, String(o.balance)),
					t.td({ class: td }, String(o.member_count)),
					t.td({ class: td }, String(o.map_count)),
				),
			),
		)
	})
	void reload_orgs()
	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Primării", msg),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-3" },
			"Click pe o primărie pentru fișa ei completă.",
		),
		t.div(
			{ class: "overflow-x-auto mb-3" },
			t.table(
				{ class: "w-full" },
				t.thead(
					t.tr(
						t.th({ class: th }, "Primărie"),
						t.th({ class: th }, "Tier"),
						t.th({ class: th }, "Status"),
						t.th({ class: th }, "Sold"),
						t.th({ class: th }, "Membri"),
						t.th({ class: th }, "Hărți"),
					),
				),
				tbody,
			),
		),
		t.div(
			{ class: "flex items-center gap-2 flex-wrap" },
			t.input({
				class: input,
				placeholder: "slug (ex. primaria_x)",
				value: slug,
				oninput: (e: Event) => (slug.val = (e.target as HTMLInputElement).value),
			}),
			t.input({
				class: input,
				placeholder: "Nume primărie",
				value: name,
				oninput: (e: Event) => (name.val = (e.target as HTMLInputElement).value),
			}),
			t.button({ class: btn, onclick: add }, "Adaugă primărie"),
		),
	)
}

function OrgFisa() {
	interface OverviewMap {
		slug: string
		name: string
		published: boolean
		layers: number
	}
	const overview = van.state<{ maps: OverviewMap[]; open_requests: number } | null>(null)
	const kpi = van.state<{ balance: number; acte: number; regim: number; vizite: number } | null>(
		null,
	)
	const free_maps = van.state<Array<{ slug: string; name: string }>>([])
	const { el: msg, set: say } = msg_line()

	async function load() {
		overview.val = null
		kpi.val = null
		const id = fisa_org.val
		if (!id) {
			return
		}
		const [ov, acc, us, maps] = await Promise.all([
			api_get<{ maps: OverviewMap[]; open_requests: number }>(`/api/admin/orgs/${id}/overview`),
			api_get<{ balance: number }>(`/api/orgs/${id}/billing`),
			api_get<{ by_kind: Record<string, number> }>(`/api/admin/orgs/${id}/usage?days=30`),
			api_get<Array<{ slug: string; name: string; org_id: string | null }>>("/api/maps"),
		])
		overview.val = ov.ok ? ov.data : { maps: [], open_requests: 0 }
		kpi.val = {
			balance: acc.ok ? acc.data.balance : 0,
			acte: us.ok ? (us.data.by_kind.document_generated ?? 0) : 0,
			regim: us.ok ? (us.data.by_kind.regim ?? 0) : 0,
			vizite: us.ok ? (us.data.by_kind.public_map_view ?? 0) : 0,
		}
		free_maps.val = maps.ok ? maps.data.filter((m) => m.org_id == null) : []
	}
	van.derive(() => {
		if (fisa_org.val) {
			void load()
		}
	})

	async function assign_map(slug: string) {
		if (!slug) {
			return
		}
		const r = await api_patch(`/api/maps/${encodeURIComponent(slug)}`, { org_id: fisa_org.val })
		say(r.ok ? "Hartă asignată ✓" : (r.err?.message ?? "Eroare"))
		await Promise.all([load(), reload_orgs()])
	}
	async function set_status(status: string) {
		const r = await api_patch(`/api/admin/orgs/${fisa_org.val}`, { status })
		say(r.ok ? "Salvat ✓" : (r.err?.message ?? "Eroare"))
		await reload_orgs()
	}

	const kpi_card = (label: string, val: () => string) =>
		t.div(
			{
				class:
					"rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 flex-1 min-w-[130px]",
			},
			t.div({ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100" }, val),
			t.div(
				{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500" },
				label,
			),
		)

	const header = t.div()
	van.derive(() => {
		const o = orgs_state.val.find((x) => x.id === fisa_org.val)
		if (!o) {
			header.replaceChildren()
			return
		}
		header.replaceChildren(
			t.div(
				{ class: "flex items-center gap-3 flex-wrap" },
				t.button({ class: btn_ghost, onclick: () => (fisa_org.val = "") }, "← Primării"),
				t.h1({ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100" }, o.name),
				t.span({ class: "text-sm text-slate-400 dark:text-slate-500 font-mono" }, o.slug),
				t.span(
					{
						class:
							"px-2 py-0.5 rounded-full text-xs font-semibold " +
							(o.status === "active"
								? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300"
								: "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300"),
					},
					o.status === "active" ? "activă" : "suspendată",
				),
				t.span({ class: "text-xs text-slate-500 dark:text-slate-400" }, tier_of(o)),
				t.button(
					{
						class:
							btn_ghost +
							(o.status === "active"
								? " text-red-600 dark:text-red-400"
								: " text-emerald-700 dark:text-emerald-300"),
						onclick: () => set_status(o.status === "active" ? "suspended" : "active"),
					},
					o.status === "active" ? "Suspendă" : "Reactivează",
				),
				msg,
			),
		)
	})

	const maps_panel = t.div()
	van.derive(() => {
		const ov = overview.val
		const sel = t.select(
			{ class: input, onchange: (e: Event) => assign_map((e.target as HTMLSelectElement).value) },
			t.option({ value: "" }, "+ asignează o hartă neasignată…"),
			...free_maps.val.map((m) => t.option({ value: m.slug }, m.name || m.slug)),
		)
		maps_panel.replaceChildren(
			t.div(
				{
					class:
						"text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
				},
				"Hărți",
			),
			...(ov?.maps ?? []).map((m) =>
				t.div(
					{ class: "flex items-center gap-2 text-sm py-1 flex-wrap" },
					t.span(
						{ class: m.published ? "text-emerald-600 dark:text-emerald-400" : "text-slate-300" },
						"●",
					),
					t.a(
						{
							class: "font-semibold text-cyan-800 dark:text-cyan-300 hover:underline",
							href: `#/map?map=${m.slug}`,
						},
						m.name,
					),
					t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, `${m.layers} straturi`),
					t.span(
						{
							class:
								"text-xs " +
								(m.published
									? "text-emerald-600 dark:text-emerald-400"
									: "text-slate-400 dark:text-slate-500"),
						},
						m.published ? "publicată" : "nepublicată",
					),
				),
			),
			(ov?.maps ?? []).length === 0
				? t.p({ class: "text-sm text-slate-400 dark:text-slate-500 py-1" }, "Nicio hartă asignată.")
				: t.span({ style: "display:none" }),
			free_maps.val.length > 0 ? t.div({ class: "mt-2" }, sel) : t.span({ style: "display:none" }),
		)
	})

	return t.div(
		{ class: () => (fisa_org.val ? "" : "hidden") },
		header,
		t.div(
			{ class: "flex gap-3 flex-wrap my-4" },
			kpi_card("Sold credite", () => String(kpi.val?.balance ?? "…")),
			kpi_card("Acte 30 zile", () => String(kpi.val?.acte ?? "…")),
			kpi_card("Interogări regim 30z", () => String(kpi.val?.regim ?? "…")),
			kpi_card("Vizite portal 30z", () => String(kpi.val?.vizite ?? "…")),
			kpi_card("Solicitări deschise", () => String(overview.val?.open_requests ?? "…")),
		),
		t.div({ class: card }, maps_panel),
		FeaturesSection(() => fisa_org.val),
		MembersSection(() => fisa_org.val),
		OrgChartSection(() => fisa_org.val),
		OrgSettingsSection(() => fisa_org.val),
		BillingSection(() => fisa_org.val),
		UsageSection(() => fisa_org.val),
		McpSection(() => fisa_org.val),
	)
}

export function AdminConsole() {
	// Full-width cu limită doar pe ecrane FOARTE late (max-w-screen-2xl ≈ 1536px) — fișa are
	// KPI-uri + două zone; sub ~900px tabelele oricum își fac scroll propriu.
	return t.div({ class: "max-w-screen-2xl mx-auto px-3 sm:px-6 py-8" }, () => {
		const s = session.val
		if (!s.authenticated) {
			return t.div(
				{ class: "text-center py-20" },
				t.p({ class: "text-slate-600 dark:text-slate-300 mb-4" }, "Trebuie să fii autentificat."),
				t.a({ href: "#/autentificare", class: btn }, "Autentificare"),
			)
		}
		const can_members =
			is_super() || s.memberships.some((m) => m.role === "owner" || m.role === "admin")
		if (!is_super() && !is_support_role() && !can_members) {
			return t.div(
				{ class: "text-center py-20 text-slate-600 dark:text-slate-300" },
				"Nu ai drepturi de administrare.",
			)
		}
		if (is_super()) {
			// Vederea ORG-CENTRICĂ: lista primăriilor ↔ fișa uneia (toate secțiunile, fără
			// selectoare repetate). Userii de platformă rămân o secțiune globală sub listă.
			// Tichetele de suport (F14) sunt GLOBALE (toate primăriile) — deasupra listei.
			return t.div(
				t.h1(
					{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-6" },
					"Administrare",
				),
				t.div(
					{ class: () => (fisa_org.val ? "hidden" : "") },
					SupportSection(),
					OrgListTable(),
					UsersSection(),
				),
				OrgFisa(),
			)
		}
		// Echipa de suport a platformei (rol `support`): DOAR inbox-ul de tichete (F14, D16).
		if (is_support_role()) {
			return t.div(
				t.h1(
					{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-6" },
					"Suport",
				),
				SupportSection(),
			)
		}
		return t.div(
			t.h1(
				{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-6" },
				"Administrare",
			),
			can_members ? MembersSection() : t.span({ style: "display:none" }),
			can_members ? OrgChartSection() : t.span({ style: "display:none" }),
			can_members ? OrgSettingsSection() : t.span({ style: "display:none" }),
			can_members ? McpSection() : t.span({ style: "display:none" }),
		)
	})
}
